import * as React from "react";
import { Navigate, Route, Routes, useLocation, useParams } from "react-router";
import { usePageView } from "@/hooks/use-page-view";
import { AuthLayout } from "@/views/auth/layout";
import { LoginView } from "@/views/auth/login";
import { ResetPasswordView } from "@/views/auth/reset-password";
import { ResetPasswordSetNewView } from "@/views/auth/reset-password-set-new";
import { ConfirmEmailChangeView } from "@/views/auth/email-change/ConfirmEmailChangeView";
import { RevertEmailChangeView } from "@/views/auth/email-change/RevertEmailChangeView";
import { RootLayout } from "@/layouts/root-layout";
import { HomeView } from "@/views/home";
import { UsersView } from "@/views/admin/users/page";
import { BusinessReportsView } from "@/modules/snapshots";
import { BusinessReportGenerateView } from "@/modules/snapshots/generate";
import { BusinessReportOutcomeDetailsView } from "@/modules/snapshots/outcome-details";
import { RootDriveView } from "@/views/drive/page";
import { SubfolderDriveView } from "@/views/drive/details";
import { ChatView } from "@/modules/chat/views/ChatView";
import { SharedView } from "@/views/shared";
import { SessionExpiredView } from "@/views/auth/session-expired";
import TopBarProgress from "react-topbar-progress-indicator";
import { SharepointView } from "@/modules/sharepoint/SharepointView";
import { OpenChatView } from "@/modules/open-chat";
import { OpenChatWithParamsView } from "@/modules/open-chat/index-with-params";
import { SnapshotEventsPage } from "@/modules/snapshots/snapshotEvents/snapshotEventsPage";
import { ContractFamiliesRouteGate } from "@/contract-families-route-gate";
import { PromptLibraryView } from "@/modules/promptLibrary/components/promptLibraryView";
import { TaskSubmittedSuccess } from "@/views/ConfirmSnapshotEventStatus";
import { ChatEmptyView } from "@/modules/chat/views/ChatEmptyView";
import { SnapshotGridView } from "@/modules/snapshots/grid";
import { SnapshotViewGrid } from "@/modules/snapshots/snapshotViews/SnapshotViewGrid";

import "react-toastify/dist/ReactToastify.css";
import { RecentQAView } from "@/modules/recent-qa";
import { EsgLayout } from "@/layouts/esg-layout";
import { EsgPortalView, EsgChatView } from "@/modules/esg-portal";
import { SuppliersRouteGate } from "@/components/suppliers/suppliers-route-gate";
import { SuppliersPage } from "@/views/suppliers/suppliers-page";
import { SupplierDetailPage } from "@/views/suppliers/supplier-detail-page";
import { ExchangeRatesPage } from "@/views/spend/exchange-rates-page";
import { SpendReportsPage } from "@/views/spend/reports/spend-reports-page";
import { SpendPage } from "@/views/spend/spend-page";
import { RenewalCentrePage } from "@/views/renewals/renewal-centre-page";
import { RenewalDetailPage } from "@/views/renewals/renewal-detail-page";
import { SupplierSpendPage } from "@/views/spend/supplier-spend-page";
import { DocumentAuthoringRouteGate } from "@/modules/documentAuthoring/DocumentAuthoringRouteGate";
import { OrganizationFeatureRouteGate } from "@/components/feature-gate/organization-feature-route-gate";
import { AuthoringView } from "@/modules/documentAuthoring/views/AuthoringView";

TopBarProgress?.config({
  barThickness: 7,
  barColors: { "0": "rgba(198, 174, 126, 1)", "1.0": "rgba(148, 118, 72, 1)" },
  shadowBlur: 5,
});

// TODO(2027-08-18): retire together with the /my-calendar routes below.
const LegacyMyCalendarRedirect: React.FC = () => {
  const { tab } = useParams<{ tab: string }>();
  const { search } = useLocation();
  return <Navigate replace to={`/snapshot-events${tab ? `/${tab}` : ""}${search}`} />;
};

export const App: React.FC = () => {
  usePageView();
  return (
    <Routes>
      <Route element={<AuthLayout />}>
        <Route path="/session-expired" element={<SessionExpiredView />} />
        <Route path="login" element={<LoginView />} />
        <Route path="reset-password" element={<ResetPasswordView />} />
        <Route path="reset-password/:token" element={<ResetPasswordSetNewView />} />
        <Route path="email-change/confirm" element={<ConfirmEmailChangeView />} />
        <Route path="email-change/revert" element={<RevertEmailChangeView />} />
        <Route path="confirm-snapshot-event-status" element={<TaskSubmittedSuccess />} />
      </Route>
      <Route element={<EsgLayout />}>
        <Route path="esg" element={<EsgPortalView />} />
        <Route path="esg/:slug" element={<EsgChatView />} />
        <Route path="esg/:slug/:conversationId" element={<EsgChatView />} />
      </Route>
      <Route element={<RootLayout />}>
        <Route element={<OrganizationFeatureRouteGate feature="open_chat" />}>
          <Route index={true} element={<HomeView />} />
          <Route path="open-chat" element={<OpenChatView />} />
          <Route path="open-chat/:conversationId" element={<OpenChatWithParamsView />} />
        </Route>
        <Route path="admin/users" element={<UsersView />} />
        <Route path="drive" element={<RootDriveView />} />
        <Route path="shared" element={<SharedView />} />
        <Route path="snapshot-events" element={<SnapshotEventsPage />} />
        <Route path="snapshot-events/:tab" element={<SnapshotEventsPage />} />
        {/* TODO(2027-08-18): retire this redirect. /my-calendar is the legacy Events Dashboard
            path; already-sent snapshot-event reminder emails link to
            /my-calendar/snapshotEvents?eventId=..., so keep it for one year (until 2027-08-18). */}
        <Route path="my-calendar" element={<LegacyMyCalendarRedirect />} />
        <Route path="my-calendar/:tab" element={<LegacyMyCalendarRedirect />} />
        <Route element={<OrganizationFeatureRouteGate feature="sharepoint" />}>
          <Route path="sharepoint" element={<SharepointView />} />
          <Route path="sharepoint/*" element={<SharepointView />} />
        </Route>
        <Route path="drive/:fseId" element={<SubfolderDriveView />} />
        <Route path="chat/:systemId" element={<ChatEmptyView />} />
        <Route path="chat/:systemId/:conversationId" element={<ChatView />} />
        <Route path="recent-qa" element={<RecentQAView />} />
        <Route path="reports" element={<BusinessReportsView />} />
        <Route path="snapshots" element={<BusinessReportsView />} />
        <Route path="snapshots/grid/:businessReport/:collectionId" element={<SnapshotGridView />} />
        <Route path="snapshots/views/:viewId" element={<SnapshotViewGrid />} />
        <Route path="snapshots/:tab" element={<BusinessReportsView />} />
        <Route path="reports/:fseId" element={<BusinessReportGenerateView />} />
        <Route path="reports/outcome/:outcomeId" element={<BusinessReportOutcomeDetailsView />} />
        <Route path="prompt-library" element={<PromptLibraryView />} />
        {/* TODO(2027-08-31): retire the sunset redirect for stale bookmarks */}
        <Route path="compare-documents/*" element={<Navigate replace to="/drive" />} />
        <Route path="contract-families/:folderId?" element={<ContractFamiliesRouteGate />} />
        <Route element={<SuppliersRouteGate />}>
          <Route path="suppliers" element={<SuppliersPage />} />
          <Route path="suppliers/:supplierId" element={<SupplierDetailPage />} />
        </Route>
        <Route element={<SuppliersRouteGate requireSpendCentre />}>
          <Route path="spend-centre" element={<SpendPage />} />
          <Route path="spend-centre/reports" element={<SpendReportsPage />} />
          <Route path="spend-centre/exchange-rates" element={<ExchangeRatesPage />} />
          <Route path="spend-centre/suppliers/:supplierId" element={<SupplierSpendPage />} />
        </Route>
        <Route element={<SuppliersRouteGate requireRenewalCentre />}>
          <Route path="renewal-centre" element={<RenewalCentrePage />} />
          <Route path="renewal-centre/:relationId" element={<RenewalDetailPage />} />
        </Route>
        <Route element={<DocumentAuthoringRouteGate />}>
          <Route path="authoring/:fseId" element={<AuthoringView />} />
        </Route>
      </Route>
    </Routes>
  );
};
